// Storage utility that falls back to sessionStorage when localStorage is unavailable
const getStorageMechanism = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return localStorage;
  } catch (e) {
    // localStorage not available, fall back to sessionStorage
    return sessionStorage;
  }
};

const storageMechanism = getStorageMechanism();

class LocalStorageWrapper {
  constructor() {
    this.isStorageAvailable = null;
  }

  getItem(key) {
    try {
      const value = storageMechanism.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  }

  setItem(key, value) {
    try {
      storageMechanism.setItem(key, JSON.stringify(value));
    } catch {}
  }

  removeItem(key) {
    try {
      storageMechanism.removeItem(key);
    } catch {}
  }

  clear() {
    try {
      storageMechanism.clear();
    } catch {}
  }

  isAvailable() {
    if (this.isStorageAvailable !== null) return this.isStorageAvailable;

    try {
      const test = '__test__';
      storageMechanism.setItem(test, test);
      storageMechanism.removeItem(test);
      this.isStorageAvailable = true;
      return this.isStorageAvailable;
    } catch {
      this.isStorageAvailable = false;
      return this.isStorageAvailable;
    }
  }
}

class IndexedDBWrapper {
  constructor() {
    this.dbName = 'kissandra';
    this.storeName = 'keyValueStore';
    this.version = 1;
    this.db = null;
    this.ready = false;
    this.initPromise = null;
  }

  async init() {
    if (this.ready) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      
      request.onsuccess = (e) => {
        this.db = e.target.result;
        this.ready = true;
        resolve();
      };
      
      request.onerror = (e) => reject(e.target.error);
    });

    return this.initPromise;
  }

  async getItem(key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, 'readonly');
      const req = tx.objectStore(this.storeName).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = (e) => reject(e.target.error);
    });
  }

  async setItem(key, value) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, 'readwrite');
      const req = tx.objectStore(this.storeName).put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = (e) => reject(e.target.error);
    });
  }

  async removeItem(key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, 'readwrite');
      const req = tx.objectStore(this.storeName).delete(key);
      req.onsuccess = () => resolve();
      req.onerror = (e) => reject(e.target.error);
    });
  }

  async clear() {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, 'readwrite');
      const req = tx.objectStore(this.storeName).clear();
      req.onsuccess = () => resolve();
      req.onerror = (e) => reject(e.target.error);
    });
  }

  isAvailable() {
    return !!window.indexedDB;
  }
}

class StorageManager {
  constructor() {
    this.local = new LocalStorageWrapper();
    this.idb = new IndexedDBWrapper();
    this.memory = new Map();
    this.prefix = '';
    this.ready = false;
    this.initPromise = null;
  }

  async init(prefix = '') {
    if (this.ready) return;
    if (this.initPromise) return this.initPromise;

    this.prefix = prefix;
    this.initPromise = this.idb.isAvailable() 
      ? this.idb.init().catch(() => {}) 
      : Promise.resolve();
    
    await this.initPromise;
    this.ready = true;
  }

  async getItem(key, opts = {}) {
    if (!this.ready) await this.init();
    
    const k = this.prefix + key;
    const { useIDB = true, useLocal = true, useMemory = true } = opts;

    if (useMemory && this.memory.has(k)) return this.memory.get(k);
    
    if (useIDB && this.idb.isAvailable()) {
      try {
        const v = await this.idb.getItem(k);
        if (v !== undefined) {
          if (useMemory) this.memory.set(k, v);
          return v;
        }
      } catch {}
    }
    
    if (useLocal && this.local.isAvailable()) {
      const v = this.local.getItem(k);
      if (v !== null) {
        if (useMemory) this.memory.set(k, v);
        if (useIDB && this.idb.isAvailable()) {
          this.idb.setItem(k, v).catch(() => {});
        }
        return v;
      }
    }
    
    return null;
  }

  async setItem(key, value, opts = {}) {
    if (!this.ready) await this.init();
    
    const k = this.prefix + key;
    const { useIDB = true, useLocal = true, useMemory = true } = opts;
    let valueToStore = value;

    // Guard against stale-object races that accidentally overwrite a resolved
    // conversationId with null/empty while a conversation is active.
    if (
      key === 'currentHangoutConfig'
      && value
      && typeof value === 'object'
      && !Array.isArray(value)
    ) {
      const existingConfig = this.local.getItem(k) || {};
      const existingConversationId = typeof existingConfig.conversationId === 'string'
        ? existingConfig.conversationId.trim()
        : '';
      const incomingConversationId = typeof value.conversationId === 'string'
        ? value.conversationId.trim()
        : '';
      const hasIncomingConversationId = incomingConversationId.length > 0;
      const isExplicitConversationReset =
        Object.prototype.hasOwnProperty.call(value, 'conversationId')
        && value.conversationId == null
        && (
          value.isNewConversation === true
          || value.forceConversationIdReset === true
        );

      if (!hasIncomingConversationId && existingConversationId && !isExplicitConversationReset) {
        valueToStore = {
          ...value,
          conversationId: existingConversationId
        };
      }
    }

    if (useMemory) this.memory.set(k, valueToStore);
    if (useIDB && this.idb.isAvailable()) {
      this.idb.setItem(k, valueToStore).catch(() => {});
    }
    if (useLocal && this.local.isAvailable()) {
      this.local.setItem(k, valueToStore);
    }
  }

  async removeItem(key, opts = {}) {
    if (!this.ready) await this.init();
    
    const k = this.prefix + key;
    const { useIDB = true, useLocal = true, useMemory = true } = opts;

    if (useMemory) this.memory.delete(k);
    if (useIDB && this.idb.isAvailable()) {
      this.idb.removeItem(k).catch(() => {});
    }
    if (useLocal && this.local.isAvailable()) {
      this.local.removeItem(k);
    }
  }

  async clear(opts = {}) {
    if (!this.ready) await this.init();
    
    const { useIDB = true, useLocal = true, useMemory = true } = opts;

    if (useMemory) this.memory.clear();
    if (useIDB && this.idb.isAvailable()) {
      try {
        await this.idb.clear();
      } catch {}
    }
    if (useLocal && this.local.isAvailable()) {
      this.local.clear();
    }
  }
}

export const storageManager = new StorageManager();
